import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Text,
  Spinner,
  MessageBar,
  MessageBarBody,
  Button,
  Input,
  Field,
  Dropdown,
  Option,
  Card,
  CardHeader,
  Badge,
  Table,
  TableHeader,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import {
  listPartners,
  getPartner,
  savePartner,
  listWidgetVersions,
  listWidgetAssets,
  uploadWidgetAsset,
  publishWidget,
  type PartnerConfig,
  type PartnerSummary,
  type WidgetAsset,
} from '../../api/partnerClient';
import { useAuthArgs } from '../../hooks/useAuthArgs';

const useStyles = makeStyles({
  grid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: tokens.spacingHorizontalL, alignItems: 'start' },
  card: { padding: tokens.spacingVerticalM },
  row: { display: 'flex', gap: tokens.spacingHorizontalS, alignItems: 'end', flexWrap: 'wrap', marginTop: tokens.spacingVerticalM },
});

const WIDGET_MODES = ['base', 'baseOverride', 'custom'];

/**
 * Admin > Widget Configuration. Configure a partner's chat widget: choose the delivery mode
 * (base / base+override / custom), edit branding tokens, upload custom widget bundle files, and
 * publish a bundle version to the CDN. Backed by the appDataStore.
 */
export function WidgetConfigPage() {
  const styles = useStyles();

  const [partners, setPartners] = useState<PartnerSummary[]>([]);
  const [partnerId, setPartnerId] = useState<string>(() => new URLSearchParams(window.location.hash.split('?')[1] ?? '').get('partner') ?? '');
  const [partner, setPartner] = useState<PartnerConfig | null>(null);
  const [versions, setVersions] = useState<string[]>([]);
  const [assets, setAssets] = useState<WidgetAsset[]>([]);
  const [selectedVersion, setSelectedVersion] = useState<string>('');
  const [uploadVersion, setUploadVersion] = useState<string>('v1');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const getAuthArgs = useAuthArgs();

  const loadPartners = useCallback(async () => {
    try {
      const auth = await getAuthArgs();
      if (!auth) {
        setError('You must be signed in to manage widgets.');
        return;
      }
      setPartners(await listPartners(auth));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, [getAuthArgs]);

  const loadPartner = useCallback(async (id: string) => {
    if (!id) { setPartner(null); return; }
    setLoading(true);
    setError(null);
    try {
      const auth = await getAuthArgs();
      if (!auth) {
        setError('You must be signed in to manage widgets.');
        return;
      }
      const [p, vs] = await Promise.all([getPartner(auth, id), listWidgetVersions(auth, id)]);
      setPartner(p);
      setVersions(vs);
      setSelectedVersion(vs[0] ?? '');
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [getAuthArgs]);

  useEffect(() => { void loadPartners(); }, [loadPartners]);
  useEffect(() => { if (partnerId) void loadPartner(partnerId); }, [partnerId, loadPartner]);

  useEffect(() => {
    (async () => {
      if (!partnerId || !selectedVersion) { setAssets([]); return; }
      try {
        const auth = await getAuthArgs();
        if (!auth) { setAssets([]); return; }
        setAssets(await listWidgetAssets(auth, partnerId, selectedVersion));
      } catch { setAssets([]); }
    })();
  }, [partnerId, selectedVersion, getAuthArgs]);

  const branding = useMemo(() => partner?.widget?.brandingTokens ?? {}, [partner]);

  const setMode = useCallback(async (mode: string) => {
    if (!partner) return;
    setError(null);
    try {
      const auth = await getAuthArgs();
      if (!auth) {
        setError('You must be signed in to save.');
        return;
      }
      const updated: PartnerConfig = { ...partner, widget: { ...(partner.widget ?? { brandingTokens: {}, overrides: {} }), mode } };
      setPartner(await savePartner(auth, updated));
      setNotice(`Widget mode set to "${mode}".`);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, [partner, getAuthArgs]);

  const saveBranding = useCallback(async (key: string, value: string) => {
    if (!partner) return;
    const widget = partner.widget ?? { mode: 'base', brandingTokens: {}, overrides: {} };
    const updated: PartnerConfig = { ...partner, widget: { ...widget, brandingTokens: { ...widget.brandingTokens, [key]: value } } };
    setPartner(updated);
  }, [partner]);

  const persistBranding = useCallback(async () => {
    if (!partner) return;
    try {
      const auth = await getAuthArgs();
      if (!auth) {
        setError('You must be signed in to save.');
        return;
      }
      setPartner(await savePartner(auth, partner));
      setNotice('Branding saved.');
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, [partner, getAuthArgs]);

  const onUpload = useCallback(async (files: FileList | null) => {
    if (!files || files.length === 0 || !partnerId) return;
    setError(null);
    try {
      const auth = await getAuthArgs();
      if (!auth) {
        setError('You must be signed in to upload.');
        return;
      }
      for (const file of Array.from(files)) {
        await uploadWidgetAsset(auth, partnerId, uploadVersion, file, file.name);
      }
      setNotice(`Uploaded ${files.length} file(s) to ${uploadVersion}.`);
      await loadPartner(partnerId);
      setSelectedVersion(uploadVersion);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, [partnerId, uploadVersion, loadPartner, getAuthArgs]);

  const onPublish = useCallback(async () => {
    if (!partnerId || !selectedVersion) return;
    setError(null);
    try {
      const auth = await getAuthArgs();
      if (!auth) {
        setError('You must be signed in to publish.');
        return;
      }
      const result = await publishWidget(auth, partnerId, selectedVersion);
      setNotice(result.cdnJsUrl ? `Published. CDN URL: ${result.cdnJsUrl}` : 'Published (no CDN base configured).');
      await loadPartner(partnerId);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, [partnerId, selectedVersion, loadPartner, getAuthArgs]);

  return (
    <section>
      <Text as="h1" size={600} weight="semibold" style={{ display: 'block', marginBottom: 16 }}>Widget Configuration</Text>

      {error && <MessageBar intent="error" style={{ marginBottom: 16 }}><MessageBarBody>{error}</MessageBarBody></MessageBar>}
      {notice && <MessageBar intent="success" style={{ marginBottom: 16 }}><MessageBarBody>{notice}</MessageBarBody></MessageBar>}

      <Field label="Partner" style={{ maxWidth: 320, marginBottom: 16 }}>
        <Dropdown
          placeholder="Select a partner"
          value={partnerId}
          selectedOptions={partnerId ? [partnerId] : []}
          onOptionSelect={(_, d) => d.optionValue && setPartnerId(d.optionValue)}
        >
          {partners.map((p) => <Option key={p.partnerId} value={p.partnerId}>{p.displayName ?? p.partnerId}</Option>)}
        </Dropdown>
      </Field>

      {loading ? (
        <Spinner label="Loading widget…" />
      ) : partner ? (
        <div className={styles.grid}>
          <Card className={styles.card}>
            <CardHeader header={<Text weight="semibold">Delivery mode &amp; branding</Text>} />
            <div className={styles.row}>
              <Field label="Mode">
                <Dropdown value={partner.widget?.mode ?? 'base'} selectedOptions={[partner.widget?.mode ?? 'base']}
                  onOptionSelect={(_, d) => d.optionValue && void setMode(d.optionValue)}>
                  {WIDGET_MODES.map((m) => <Option key={m} value={m}>{m}</Option>)}
                </Dropdown>
              </Field>
              {partner.widget?.cdnJsUrl && <Badge appearance="tint" color="brand">published: {partner.widget.publishedVersion}</Badge>}
            </div>
            <div style={{ marginTop: 12 }}>
              <Field label="Primary color">
                <input type="color" value={branding.primaryColor ?? '#107C10'} onChange={(e) => void saveBranding('primaryColor', e.target.value)} style={{ width: 48, height: 32, border: 'none', background: 'none' }} />
              </Field>
              <Field label="Widget name" style={{ marginTop: 8 }}>
                <Input value={branding.name ?? ''} placeholder="Xbox Assistant" onChange={(_, d) => void saveBranding('name', d.value)} />
              </Field>
              <Button appearance="primary" style={{ marginTop: 12 }} onClick={() => void persistBranding()}>Save branding</Button>
            </div>
            {partner.widget?.cdnJsUrl && (
              <div style={{ marginTop: 12 }}>
                <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>CDN URL</Text>
                <div><code>{partner.widget.cdnJsUrl}</code></div>
              </div>
            )}
          </Card>

          <Card className={styles.card}>
            <CardHeader header={<Text weight="semibold">Custom widget bundle</Text>} />
            <div className={styles.row}>
              <Field label="Upload to version"><Input value={uploadVersion} onChange={(_, d) => setUploadVersion(d.value)} /></Field>
              <input type="file" multiple onChange={(e) => void onUpload(e.target.files)} />
            </div>

            <div className={styles.row}>
              <Field label="Version">
                <Dropdown placeholder="version" value={selectedVersion} selectedOptions={selectedVersion ? [selectedVersion] : []}
                  onOptionSelect={(_, d) => d.optionValue && setSelectedVersion(d.optionValue)}>
                  {versions.map((v) => <Option key={v} value={v}>{v}</Option>)}
                </Dropdown>
              </Field>
              <Button appearance="primary" disabled={!selectedVersion} onClick={() => void onPublish()}>Publish to CDN</Button>
            </div>

            <Table aria-label="Widget assets" style={{ marginTop: 12 }}>
              <TableHeader>
                <TableRow><TableHeaderCell>File</TableHeaderCell><TableHeaderCell>Size</TableHeaderCell></TableRow>
              </TableHeader>
              <TableBody>
                {assets.length === 0 ? (
                  <TableRow><TableCell colSpan={2}><Text italic>No files in this version.</Text></TableCell></TableRow>
                ) : (
                  assets.map((a) => (
                    <TableRow key={a.path}>
                      <TableCell>{a.path}</TableCell>
                      <TableCell>{formatBytes(a.length)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Card>
        </div>
      ) : (
        <Text italic>Select a partner to configure its widget.</Text>
      )}
    </section>
  );
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}
